import { createDefaultWorldConfig, type WorldConfig } from "@xiuxian/game-core";
import type {
  LeaderboardEntry,
  PeriodLeaderboards,
  ServerErrorMessage,
  ServerRoomStateMessage,
  ServerSnapshotMessage,
  ServerWelcomeMessage,
  TreasureKind,
  WelcomeRoom,
} from "@xiuxian/game-protocol";
import {
  formatRuntimeLog,
  formatRuntimeStatus,
} from "./i18n/hud-formatters.js";
import { DEFAULT_LOCALE, type Locale } from "./i18n/index.js";

export type RuntimeConnectionStatus =
  | "idle"
  | "connecting"
  | "ready"
  | "disconnected"
  | "error";

export interface RuntimeViewState {
  connectionStatus: RuntimeConnectionStatus;
  statusMessage: string;
  disconnectReason: string | null;
  lastError: string | null;
  welcome: ServerWelcomeMessage | null;
  /** Room context from the welcome (null/official => standard quick play). */
  roomInfo: WelcomeRoom | null;
  /** Latest private-room waiting-lobby state, null outside a private room. */
  waitingRoom: ServerRoomStateMessage | null;
  /** Private-room match settlement, set when the match ends. */
  matchSettlement: MatchSettlementView | null;
  /** Private-room chat log (most recent last), capped in length. */
  chatMessages: ChatMessageView[];
  lastSnapshot: ServerSnapshotMessage | null;
  lastLeaderboard: LeaderboardEntry[];
  lastPeriodLeaderboards: PeriodLeaderboards | null;
  lastSettledScore: { score: number; reason: string } | null;
  deathSpectate: DeathSpectateView | null;
  lastAscensionAnnouncement: string | null;
  ascensionRitual: AscensionRitualView | null;
  lastTreasureAnnouncement: TreasureAnnouncement | null;
  lastTreasurePickup: TreasureAnnouncement | null;
  lastTreasureGuard: TreasureGuardAnnouncement | null;
  lastServerError: ServerErrorMessage | null;
  reconnectAt: number | null;
  receivedMessageCount: number;
  eventLog: string[];
  pickupEffects: PickupEffect[];
  attackEffects: AttackEffect[];
  dropBurstEffects: DropBurstEffect[];
  audioCues: AudioCue[];
  performance: RuntimePerformanceSnapshot;
  coreBaseline: WorldConfig;
}

export interface RuntimePerformanceSnapshot {
  fps: number;
  updateMs: number;
  updateMsAverage: number;
  updateMsMax: number;
  drawWorldMs: number;
  drawWorldMsAverage: number;
  drawWorldMsMax: number;
  frameDeltaMs: number;
  frameDeltaMsAverage: number;
  longFrames16: number;
  longFrames25: number;
  longFrames33: number;
  visiblePlayers: number;
  visibleStones: number;
  stoneSimple: number;
  stoneMedium: number;
  stoneFull: number;
  pickupEffects: number;
  attackEffects: number;
  displayObjects: number;
  playerSprites: number;
  formationSprites: number;
  swordSprites: number;
  messagesPerSecond: number;
  heapUsedMb: number | null;
  receivedMessageCount: number;
}

export interface DeathSpectateView {
  selfId: string;
  targetId: string;
  startedAt: number;
}

export interface MatchStandingView {
  id: string;
  name: string;
  score: number;
  isAlive: boolean;
  /** Team of this player in team matches; null/absent in solo modes. */
  teamId?: string | null;
}

export interface TeamScoreView {
  teamId: string;
  score: number;
}

export interface MatchSettlementView {
  winnerId: string | null;
  winnerName: string | null;
  winCondition: string;
  /** Winning team id for team matches; null otherwise. */
  winningTeam: string | null;
  /** Per-team final scores for team matches; empty in solo modes. */
  teams: TeamScoreView[];
  standings: MatchStandingView[];
}

export interface ChatMessageView {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  sentAt: number;
}

export interface PickupEffect {
  id: string;
  playerId: string;
  stoneId: string;
  x: number;
  y: number;
  value: number;
  createdAt: number;
}

export type AttackEffectResult = "started" | "hit" | "missed";

export interface AttackEffect {
  id: string;
  result: AttackEffectResult;
  attackerId: string;
  targetId: string | null;
  attackerX: number | null;
  attackerY: number | null;
  targetX: number | null;
  targetY: number | null;
  facing: number | null;
  durationMs: number;
  createdAt: number;
}

export interface DropBurstEffect {
  id: string;
  playerId: string;
  x: number;
  y: number;
  value: number;
  createdAt: number;
}

export interface AscensionRitualView {
  ascenderId: string;
  ascenderName: string;
  scoreGain: number;
  totalScore: number;
  startedAt: number;
  endsAt: number;
  isSelf: boolean;
  phase: "ascending" | "reborn";
  rebornAt: number | null;
}

export interface TreasureAnnouncement {
  id: string;
  kind: TreasureKind;
  x: number;
  y: number;
  createdAt: number;
}

export interface TreasureGuardAnnouncement {
  playerId: string;
  attackerId: string;
  remainingCharges: number;
  x: number;
  y: number;
  createdAt: number;
}

export type AudioCueKind =
  | "ui_focus"
  | "ui_start"
  | "ui_reincarnate"
  | "ui_error"
  | "pickup_low"
  | "pickup_mid"
  | "pickup_high"
  | "treasure_spawned"
  | "treasure_acquired"
  | "stone_scatter"
  | "level_up"
  | "sword_windup"
  | "sword_release"
  | "sword_hit"
  | "sword_miss"
  | "soft_bump"
  | "death_scatter"
  | "reincarnation_fade";

export interface AudioCue {
  id: string;
  kind: AudioCueKind;
  volumeScale: number;
  detune: number;
  createdAt: number;
}

type RuntimeListener = (state: RuntimeViewState) => void;

const MAX_LOG_LINES = 8;
const MAX_CHAT_MESSAGES = 50;
export const MAX_PICKUP_EFFECTS = 36;
export const MAX_ATTACK_EFFECTS = 48;
export const MAX_DROP_BURST_EFFECTS = 18;
const MAX_AUDIO_CUES = 48;

export class RuntimeStateStore {
  private state: RuntimeViewState;

  private readonly listeners = new Set<RuntimeListener>();

  constructor(locale: Locale = DEFAULT_LOCALE) {
    this.state = {
      connectionStatus: "idle",
      statusMessage: formatRuntimeStatus(locale, "default"),
      disconnectReason: null,
      lastError: null,
      welcome: null,
      roomInfo: null,
      waitingRoom: null,
      matchSettlement: null,
      chatMessages: [],
      lastSnapshot: null,
      lastLeaderboard: [],
      lastPeriodLeaderboards: null,
      lastSettledScore: null,
      deathSpectate: null,
      lastAscensionAnnouncement: null,
      ascensionRitual: null,
      lastTreasureAnnouncement: null,
      lastTreasurePickup: null,
      lastTreasureGuard: null,
      lastServerError: null,
      reconnectAt: null,
      receivedMessageCount: 0,
      eventLog: [formatRuntimeLog(locale, "started")],
      pickupEffects: [],
      attackEffects: [],
      dropBurstEffects: [],
      audioCues: [],
      performance: createEmptyPerformanceSnapshot(),
      coreBaseline: createDefaultWorldConfig(),
    };
  }

  getState(): RuntimeViewState {
    return this.state;
  }

  subscribe(listener: RuntimeListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  patch(partial: Partial<RuntimeViewState>): void {
    this.state = {
      ...this.state,
      ...partial,
    };
    this.emit();
  }

  recordChatMessage(message: ChatMessageView): void {
    this.state = {
      ...this.state,
      chatMessages: [...this.state.chatMessages, message].slice(
        -MAX_CHAT_MESSAGES,
      ),
    };
    this.emit();
  }

  recordLog(line: string, locale: Locale = DEFAULT_LOCALE): void {
    const timestamp = new Date().toLocaleTimeString(locale, {
      hour12: false,
    });
    this.state = {
      ...this.state,
      eventLog: [`[${timestamp}] ${line}`, ...this.state.eventLog].slice(
        0,
        MAX_LOG_LINES,
      ),
    };
    this.emit();
  }

  incrementMessageCount(): void {
    this.state = {
      ...this.state,
      receivedMessageCount: this.state.receivedMessageCount + 1,
      performance: {
        ...this.state.performance,
        receivedMessageCount: this.state.receivedMessageCount + 1,
      },
    };
    this.emit();
  }

  recordPerformanceSample(
    sample: Omit<
      RuntimePerformanceSnapshot,
      | "updateMsAverage"
      | "updateMsMax"
      | "drawWorldMsAverage"
      | "drawWorldMsMax"
      | "frameDeltaMsAverage"
      | "receivedMessageCount"
    >,
  ): void {
    const updateMsMax = Math.max(
      this.state.performance.updateMsMax,
      sample.updateMs,
    );
    const updateMsAverage =
      this.state.performance.updateMsAverage <= 0
        ? sample.updateMs
        : this.state.performance.updateMsAverage * 0.92 +
          sample.updateMs * 0.08;
    const drawWorldMsMax = Math.max(
      this.state.performance.drawWorldMsMax,
      sample.drawWorldMs,
    );
    const drawWorldMsAverage =
      this.state.performance.drawWorldMsAverage <= 0
        ? sample.drawWorldMs
        : this.state.performance.drawWorldMsAverage * 0.92 +
          sample.drawWorldMs * 0.08;
    const frameDeltaMsAverage =
      this.state.performance.frameDeltaMsAverage <= 0
        ? sample.frameDeltaMs
        : this.state.performance.frameDeltaMsAverage * 0.92 +
          sample.frameDeltaMs * 0.08;
    this.state = {
      ...this.state,
      performance: {
        ...sample,
        updateMsAverage,
        updateMsMax,
        drawWorldMsAverage,
        drawWorldMsMax,
        frameDeltaMsAverage,
        receivedMessageCount: this.state.receivedMessageCount,
      },
    };
    this.emit();
  }

  recordPickupEffect(effect: Omit<PickupEffect, "id" | "createdAt">): void {
    const createdAt = Date.now();
    const nextEffect = {
      ...effect,
      id: `${effect.playerId}:${effect.stoneId}:${createdAt.toString()}`,
      createdAt,
    };
    this.state = {
      ...this.state,
      pickupEffects: limitPickupEffects([
        nextEffect,
        ...this.state.pickupEffects,
      ]),
    };
    this.emit();
  }

  prunePickupEffects(before: number): void {
    const pickupEffects = this.state.pickupEffects.filter(
      (effect) => effect.createdAt >= before,
    );
    if (pickupEffects.length === this.state.pickupEffects.length) {
      return;
    }
    this.state = {
      ...this.state,
      pickupEffects,
    };
    this.emit();
  }

  recordAttackEffect(effect: Omit<AttackEffect, "id" | "createdAt">): void {
    const createdAt = Date.now();
    this.state = {
      ...this.state,
      attackEffects: [
        {
          ...effect,
          id: `${effect.attackerId}:${effect.result}:${createdAt.toString()}`,
          createdAt,
        },
        ...this.state.attackEffects,
      ].slice(0, MAX_ATTACK_EFFECTS),
    };
    this.emit();
  }

  pruneAttackEffects(before: number): void {
    const attackEffects = this.state.attackEffects.filter(
      (effect) => effect.createdAt >= before,
    );
    if (attackEffects.length === this.state.attackEffects.length) {
      return;
    }
    this.state = {
      ...this.state,
      attackEffects,
    };
    this.emit();
  }

  recordDropBurstEffect(
    effect: Omit<DropBurstEffect, "id" | "createdAt">,
  ): void {
    const createdAt = Date.now();
    this.state = {
      ...this.state,
      dropBurstEffects: [
        {
          ...effect,
          id: `${effect.playerId}:drop-burst:${createdAt.toString()}`,
          createdAt,
        },
        ...this.state.dropBurstEffects,
      ].slice(0, MAX_DROP_BURST_EFFECTS),
    };
    this.emit();
  }

  recordTreasureAnnouncement(
    announcement: Omit<TreasureAnnouncement, "createdAt">,
  ): void {
    this.state = {
      ...this.state,
      lastTreasureAnnouncement: {
        ...announcement,
        createdAt: Date.now(),
      },
    };
    this.emit();
  }

  recordTreasurePickup(pickup: Omit<TreasureAnnouncement, "createdAt">): void {
    this.state = {
      ...this.state,
      lastTreasurePickup: {
        ...pickup,
        createdAt: Date.now(),
      },
    };
    this.emit();
  }

  recordTreasureGuard(
    guard: Omit<TreasureGuardAnnouncement, "createdAt">,
  ): void {
    this.state = {
      ...this.state,
      lastTreasureGuard: {
        ...guard,
        createdAt: Date.now(),
      },
    };
    this.emit();
  }

  pruneDropBurstEffects(before: number): void {
    const dropBurstEffects = this.state.dropBurstEffects.filter(
      (effect) => effect.createdAt >= before,
    );
    if (dropBurstEffects.length === this.state.dropBurstEffects.length) {
      return;
    }
    this.state = {
      ...this.state,
      dropBurstEffects,
    };
    this.emit();
  }

  recordAudioCue(
    kind: AudioCueKind,
    options: { volumeScale?: number; detune?: number } = {},
  ): void {
    const createdAt = Date.now();
    this.state = {
      ...this.state,
      audioCues: [
        {
          id: `${kind}:${createdAt.toString()}:${Math.random().toString(36).slice(2)}`,
          kind,
          volumeScale: options.volumeScale ?? 1,
          detune: options.detune ?? 0,
          createdAt,
        },
        ...this.state.audioCues,
      ].slice(0, MAX_AUDIO_CUES),
    };
    this.emit();
  }

  pruneAudioCues(before: number): void {
    const audioCues = this.state.audioCues.filter(
      (cue) => cue.createdAt >= before,
    );
    if (audioCues.length === this.state.audioCues.length) {
      return;
    }
    this.state = {
      ...this.state,
      audioCues,
    };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

function createEmptyPerformanceSnapshot(): RuntimePerformanceSnapshot {
  return {
    fps: 0,
    updateMs: 0,
    updateMsAverage: 0,
    updateMsMax: 0,
    drawWorldMs: 0,
    drawWorldMsAverage: 0,
    drawWorldMsMax: 0,
    frameDeltaMs: 0,
    frameDeltaMsAverage: 0,
    longFrames16: 0,
    longFrames25: 0,
    longFrames33: 0,
    visiblePlayers: 0,
    visibleStones: 0,
    stoneSimple: 0,
    stoneMedium: 0,
    stoneFull: 0,
    pickupEffects: 0,
    attackEffects: 0,
    displayObjects: 0,
    playerSprites: 0,
    formationSprites: 0,
    swordSprites: 0,
    messagesPerSecond: 0,
    heapUsedMb: null,
    receivedMessageCount: 0,
  };
}

function limitPickupEffects(effects: PickupEffect[]): PickupEffect[] {
  return [...effects]
    .sort((left, right) => {
      if (right.value !== left.value) {
        return right.value - left.value;
      }
      return right.createdAt - left.createdAt;
    })
    .slice(0, MAX_PICKUP_EFFECTS);
}
